import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router'

import { api, can, publicApiUrl, type EndpointRow } from '../api'
import { Pill } from '../components'
import { DataGrid, type BulkAction, type GridFilter } from '../DataGrid'
import { EnvToggle } from '../EnvToggle'
import { dt, num } from '../format'

const gateState = (e: EndpointRow) => (e.gate.problems.length > 0 ? 'problems' : e.gate.findings.errors.length ? 'blocked' : 'ok')

const endpointFilters: GridFilter<EndpointRow>[] = [
  {
    key: 'prod',
    label: 'Prod',
    options: [
      { value: 'published', label: 'published' },
      { value: 'draft', label: 'draft' },
      { value: 'archived', label: 'archived' },
    ],
    value: (e) => e.status,
  },
  {
    key: 'dev',
    label: 'Dev',
    options: [
      { value: 'on', label: 'published' },
      { value: 'off', label: 'not published' },
    ],
    value: (e) => (e.dev_published_version != null ? 'on' : 'off'),
  },
  {
    key: 'gate',
    label: 'Gate',
    options: [
      { value: 'ok', label: 'can publish' },
      { value: 'blocked', label: 'blocked' },
      { value: 'problems', label: 'spec problems' },
    ],
    value: gateState,
  },
  {
    key: 'changed',
    label: 'Draft changes',
    options: [{ value: 'yes', label: 'newer than published' }],
    value: (e) => (e.status === 'published' && e.published_version !== e.current_version ? 'yes' : ''),
  },
]

const devActions: BulkAction<EndpointRow>[] = [
  {
    label: 'Publish on dev',
    primary: true,
    applies: (e) => e.status !== 'archived' && e.dev_published_version !== e.current_version,
    run: (e) => api.publishEndpointDev(e.slug, e.current_version),
  },
  { label: 'Unpublish from dev', applies: (e) => e.dev_published_version != null, run: (e) => api.unpublishEndpointDev(e.slug) },
]

export function GateBadge({ e }: { e: EndpointRow }) {
  const g = e.gate
  if (g.problems.length > 0) return <Pill kind="err">spec problems</Pill>
  if (!g.findings.errors.length) return <Pill kind="ok">can publish</Pill>
  return <Pill kind="err">{g.findings.errors.length} blocking</Pill>
}

export function Endpoints() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['endpoints'], queryFn: api.endpoints })
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const admin = can(me.data, 'admin')
  // The sandbox (dev.aleva.se) is aleva.se's own: its switches are for the
  // platform org and do not exist in an enterprise installation.
  const dev = me.data?.org_id === 'aleva' && me.data.edition !== 'enterprise'
  const actions = useMemo<BulkAction<EndpointRow>[]>(
    () =>
      admin
        ? [
            ...(dev ? devActions : []),
            {
              label: 'Publish on prod',
              primary: true,
              applies: (e) => e.status !== 'archived' && e.published_version !== e.current_version,
              run: (e) => api.publishEndpoint(e.slug, e.current_version),
            },
            { label: 'MCP on', applies: (e) => !e.mcp_enabled, run: (e) => api.setEndpointMCP(e.slug, true) },
            { label: 'MCP off', applies: (e) => e.mcp_enabled, run: (e) => api.setEndpointMCP(e.slug, false) },
            {
              label: 'Unpublish from prod',
              applies: (e) => e.status === 'published',
              confirm: 'Take these endpoints off the production API?',
              run: (e) => api.unpublishEndpoint(e.slug),
            },
          ]
        : [],
    [admin, dev],
  )

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['endpoints'] })
    qc.invalidateQueries({ queryKey: ['pages'] })
  }
  const cols = useMemo<ColDef<EndpointRow>[]>(
    () => [
      {
        field: 'path',
        headerName: 'Endpoint',
        flex: 2,
        minWidth: 260,
        cellRenderer: ({ data }: ICellRendererParams<EndpointRow>) =>
          data ? (
            <span className="cell-two">
              <Link to={`/endpoints/${data.slug}`} className="mono">
                GET {data.path}
              </Link>
              <span className="muted">{data.title}</span>
            </span>
          ) : null,
      },
      {
        field: 'bucket',
        width: 200,
        cellRenderer: ({ value }: ICellRendererParams<EndpointRow, string>) => <Link to={`/buckets/${value}`} className="mono">{value}</Link>,
      },
      {
        headerName: 'Dev',
        hide: !dev,
        width: 150,
        sortable: false,
        cellRenderer: ({ data: e }: ICellRendererParams<EndpointRow>) =>
          e ? (
            <EnvToggle label="Dev" version={e.dev_published_version} current={e.current_version} canPublish={gateState(e) === 'ok'} admin={admin}
              publish={() => api.publishEndpointDev(e.slug, e.current_version)} unpublish={() => api.unpublishEndpointDev(e.slug)} onDone={refresh} />
          ) : null,
      },
      {
        headerName: 'Prod',
        width: 150,
        sortable: false,
        cellRenderer: ({ data: e }: ICellRendererParams<EndpointRow>) =>
          e ? (
            e.status === 'archived' ? (
              <Pill kind="neutral">archived</Pill>
            ) : (
              <EnvToggle label="Prod" version={e.status === 'published' ? e.published_version : null} current={e.current_version}
                canPublish={gateState(e) === 'ok'} admin={admin} publish={() => api.publishEndpoint(e.slug, e.current_version)}
                unpublish={() => api.unpublishEndpoint(e.slug)} onDone={refresh} />
            )
          ) : null,
      },
      {
        headerName: 'MCP',
        width: 90,
        sortable: false,
        cellRenderer: ({ data: e }: ICellRendererParams<EndpointRow>) =>
          e ? (
            <input type="checkbox" role="switch" aria-label={`MCP tool: ${e.mcp_enabled ? 'on' : 'off'}`} checked={e.mcp_enabled} disabled={!admin}
              onChange={(ev) => api.setEndpointMCP(e.slug, ev.target.checked).then(refresh)} />
          ) : null,
      },
      { headerName: 'Gate (latest)', width: 150, cellRenderer: ({ data }: ICellRendererParams<EndpointRow>) => (data ? <GateBadge e={data} /> : null) },
      { field: 'current_version', headerName: 'Latest', width: 90, valueFormatter: (p) => `v${p.value}`, cellClass: 'mono' },
      { field: 'requests_7d', headerName: 'Requests 7 d', width: 130, valueFormatter: (p) => num(p.value), cellClass: 'num' },
      { field: 'published_at', headerName: 'Published', width: 140, valueFormatter: (p) => dt(p.value) },
    ],
    [admin, dev], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Endpoints</h1>
          <p className="sub">
            Each published endpoint spec is a route on <a href={`${publicApiUrl}/openapi.json`}>{publicApiUrl}</a>. Publishing needs an
            admin and passes the licence gate: every upstream licence complete, the bucket built, bulk only over exportable data.
          </p>
        </div>
        <div className="row">
          {can(me.data, 'editor') && (
            <Link className="btn btn-primary" to="/endpoints/new">
              New endpoint
            </Link>
          )}
          <a className="btn" href={`${publicApiUrl}/v1/_registry`} target="_blank" rel="noreferrer">
            Registry
          </a>
        </div>
      </header>
      {q.error && <p className="callout err">{q.error.message}</p>}
      <DataGrid<EndpointRow>
        rows={q.data}
        columnDefs={cols}
        loading={q.isLoading}
        getRowId={(e) => e.slug}
        searchText={(e) => [e.slug, e.title, e.path, e.bucket].join(' ')}
        filters={dev ? endpointFilters : endpointFilters.filter((f) => f.key !== 'dev')}
        actions={actions}
        onDone={refresh}
        onOpen={(e) => navigate(`/endpoints/${e.slug}`)}
      />
    </section>
  )
}
